import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import Translate from 'ui/atoms/translate';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import WideContent from 'core/layout/content/wide-content';
import IssuerInvestmentsTable from 'apps/issuer/shared/investments-table';
import { hasDownloadStatisticsPermission, hasViewPermission } from 'src/apps/issuer/helpers/tokenPermissions';
import DownloadInvestmentData from './download-investment-data';
import DownloadPlatformInvestmentData from './download-platform-investment-data';
import Spacer from 'src/ui/atoms/spacer';
import Box from 'src/ui/polymorphs/box';
import {
  hasDistributionDownloadPlatformStatisticsDataPermission,
  hasDistributionViewDataPermission,
} from 'apps/issuer/helpers/distributionPlatformPermissions';
import { useCurrentUserSelector } from 'src/core/auth/hooks';
import Button from 'src/ui/atoms/button';
import AddInvesmentModal from './add-investment-modal';
import { debounce } from 'lodash';
import { AdminApi, AdminProductListing, AdminProductsListPermissionEnum, ProductPermissionEnum } from 'src/api';
import useApiCall from 'src/hooks/use-api-call';
import PageWrapper from 'src/core/layout/content/page-wrapper';

const IssuerInvestmentsPage: FunctionComponent = () => {
  const [filterPopupHeight, setFilterPopupHeight] = useState(0);
  const [isAddInvestmentModalOpen, setAddInvestmentModalOpen] = useState(false);
  const [forceDataLoad, setForceDataLoad] = useState(false);
  const [products, setProducts] = useState<AdminProductListing[]>([]);

  const { currentUser } = useCurrentUserSelector();
  const { withApi, makeAuthenticatedApi } = useApiCall();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const hasDownloadPermission = hasDownloadStatisticsPermission(currentUser);
  const hasInvestmentsPermission = currentUser?.permissionOnAtLeastOneProduct.includes(
    ProductPermissionEnum.MANAGE_INVESTMENTS,
  );
  const hasDownloadPlatformStatisticsPermission = hasDistributionDownloadPlatformStatisticsDataPermission(
    currentUser?.associatedDistributionPlatforms || [],
  );
  const hasPermission =
    hasDistributionViewDataPermission(currentUser?.associatedDistributionPlatforms || []) ||
    hasViewPermission(currentUser);

  const fetchProducts = useCallback(
    (searchQuery: string = '') => {
      withApi(async () => {
        const response = await adminApi.adminProductsList({
          permission: AdminProductsListPermissionEnum.MANAGE_INVESTMENTS,
          publicationValid: true,
          search: searchQuery,
          limit: 100,
        });
        setProducts(response.results || []);
      });
    },
    [withApi, adminApi],
  );

  const searchProductValue = useCallback(
    (value: string) => {
      fetchProducts(value);
    },
    [fetchProducts],
  );

  const debouncedProductSearch = useMemo(() => debounce(searchProductValue, 500), [searchProductValue]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const refreshTableData = useCallback(() => {
    setAddInvestmentModalOpen(false);
    setForceDataLoad(true);
  }, []);

  const renderDownloadButtons = useCallback(() => {
    if (!hasDownloadPermission && !hasDownloadPlatformStatisticsPermission) return null;

    return (
      <Box>
        {hasDownloadPermission && <DownloadInvestmentData />}
        {hasDownloadPlatformStatisticsPermission && (
          <>
            <Spacer x={2} />
            <DownloadPlatformInvestmentData />
          </>
        )}
      </Box>
    );
  }, []);

  return (
    <PageWrapper paddingBottom={filterPopupHeight}>
      <WideContent
        title={<Translate name="common.investments" />}
        headerPanel={renderDownloadButtons()}
        titleSpacing="larger"
      >
        {hasInvestmentsPermission && (
          <>
            <Button
              variant="primary"
              icon="plus"
              size="small"
              iconSize="small"
              iconColor="inherit"
              onClick={() => setAddInvestmentModalOpen(true)}
            >
              <Translate name="addInvestment.title" />
            </Button>
            <Spacer y={4} />
          </>
        )}
        <IssuerInvestmentsTable
          defaultLimit={50}
          forceDataLoad={forceDataLoad}
          setForceDataLoad={setForceDataLoad}
          hasPermission={hasPermission}
          setFilterPopupHeight={setFilterPopupHeight}
        />
        {isAddInvestmentModalOpen && (
          <AddInvesmentModal
            onClose={() => setAddInvestmentModalOpen(false)}
            onAddNewInvestment={refreshTableData}
            products={products}
            onSearchProduct={debouncedProductSearch}
          />
        )}
      </WideContent>
    </PageWrapper>
  );
};

export default WithAuthenticatedPage(IssuerInvestmentsPage, AppType.ISSUER);
